<div class="container-vertical">
  <div class="process-header">
    <!-- display: contents on desktop; small screens: sheet title (process name) above the description -->
    <div class="process-heading">
      @if (isModal && processItem?.label) {
        <h2 class="process-sheet-title">{{ processItem.label }}</h2>
      }
      @if (description && description !== '-') {
        <p class="title">{{ description }}</p>
      }
    </div>
    <!-- one logs toggle: here while the panel is closed (with the number of lines); the open panel has its own
         close button in its header -->
    @if (!isLogsOpen) {
      <button class="btn-logs-toggle" type="button" [attr.aria-expanded]="false" (click)="isLogsOpen = true">
        <span class="logs-dot"></span>
        <span>{{ 'dialog.processLogs' | translate }}</span>
        @if (logContainer.lines().length) {
          <span class="logs-count">{{ logContainer.lines().length }}</span>
        }
        <i class="fa fa-chevron-left logs-toggle-icon"></i>
      </button>
    }
  </div>

  @if (help?.trim()) {
    <div class="process-description">
      <span
        class="desc-text"
        [class.expanded]="isHelpShown"
        [innerHTML]="isHelpShown ? help : help.substr(0, 200)"></span>
      @if (!isHelpShown && help.length > 200) {
        <button
          type="button"
          class="help-more"
          aria-expanded="false"
          [attr.aria-label]="'notification.more' | translate"
          (click)="isHelpShown = true">
          (...)
        </button>
      }
      @if (isHelpShown) {
        <button
          type="button"
          class="help-more"
          aria-expanded="true"
          [attr.aria-label]="'notification.less' | translate"
          (click)="isHelpShown = false">
          ▲
        </button>
      }
    </div>
  }

  <div
    #processBody
    class="process-body"
    [class.is-resizing]="isResizing"
    [class.logs-open]="isLogsOpen"
    [style.grid-template-columns]="isLogsOpen ? '1fr 6px ' + logsDrawerWidth + 'px' : null">
    <div class="params-panel">
      <div class="apiz-p-grid blade-grid" [style.--blade-nb-cols]="apizUiProcess()?.process?.nbCol">
        <ng-template iuDynamicContainer />
        @if (isReport && exportDataOptions) {
          @if (hasFormat) {
            <iu-autocomplete-ui
              #printFormatsAutocomplete
              [attr.data-cy]="'exportDataOptions-printFormats'"
              [cssClass]="'p-col-12 p-offset-0'"
              [data]="exportDataOptions.printFormats"
              [isStandalone]="true"
              label="{{ 'exportData.printFormats' | translate }}"
              tooltip="{{ 'exportData.printFormats' | translate }}"
              (fieldValueModified)="onPrintFormatChange($any($event))" />
          }
          <iu-autocomplete-ui
            #fileTypesAutocomplete
            [attr.data-cy]="'exportDataOptions-fileTypes'"
            [cssClass]="'p-col-12 p-offset-0'"
            [data]="exportDataOptions.fileTypes"
            [fieldValue]="defaultFileType"
            [isStandalone]="true"
            label="{{ 'exportData.fileTypes' | translate }}"
            tooltip="{{ 'exportData.fileTypes' | translate }}" />
        }
      </div>
      <!-- small screens only (css): shown when the grid holds just the empty params group -->
      @if (apizUiProcess()) {
        <p class="process-no-params">{{ 'process.noParams' | translate }}</p>
      }
    </div>

    <div
      class="logs-resize-handle"
      [class.active]="isLogsOpen"
      (mousedown)="onResizeStart($event)"
      (touchstart)="onResizeStart($event)"></div>

    <div class="logs-drawer">
      <div class="logs-drawer-inner">
        <!-- one header line: title, count, filter, copy, download, close -->
        <iu-process-logs-ui
          #logContainer
          [heading]="'process.logs' | translate"
          [closable]="true"
          (closed)="isLogsOpen = false" />
      </div>
    </div>
  </div>

  <div class="process-footer">
    <!-- small screens only (css): logs toggle as a light link under the params -->
    <button
      class="btn-logs-link"
      data-cy="process-logs-link"
      type="button"
      [attr.aria-expanded]="isLogsOpen"
      [class.active]="isLogsOpen"
      (click)="isLogsOpen = !isLogsOpen">
      <span>{{ 'dialog.processLogs' | translate }}</span>
      <i aria-hidden="true" class="fa" [class.fa-chevron-down]="!isLogsOpen" [class.fa-chevron-up]="isLogsOpen"></i>
    </button>
    <div class="process-status">
      @let step = steps[activeIndex];
      <!-- the initial "no action" placeholder is hidden; real statuses are always shown -->
      @if (step && !isIdleStatus(step)) {
        <span class="status-label">{{ step.label }}</span>
      }
      @if (activeIndex === 1 && processElapsedLabel()) {
        <span class="status-timing">{{ 'dialog.processStartedSince' | translate: { elapsed: processElapsedLabel() } }}</span>
      }
      @if (activeIndex === 2 && processDoneLabel()) {
        <span class="status-timing status-timing--done">{{
          'dialog.processFinishedAt' | translate: { time: processDoneLabel() }
        }}</span>
      }
      <!-- files produced by the run (report): downloaded from here, no need to find the notification -->
      @for (file of resultFiles(); track file.path) {
        <button type="button" class="process-result-file" [title]="file.fileName" (click)="downloadResult(file)">
          <i class="fa-solid fa-download" aria-hidden="true"></i>
          <span>{{ file.fileName || ('process.result' | translate) }}</span>
        </button>
      }
    </div>
    <!-- why Execute is disabled, visible (a title shows on mouse hover only) -->
    @if (!isProcessing && missingMandatoryParams.length > 0) {
      <p class="process-missing-params" role="status" data-cy="process-missing-params">
        {{ 'process.fillMandatoryField' | translate }} : {{ missingMandatoryParams.join(', ') }}
      </p>
    }
    <div class="process-actions">
      @if (isModal) {
        <button
          class="p-button p-button-secondary"
          data-cy="process-close-button"
          icon="fa fa-ban"
          pButton
          type="button"
          label="{{ 'generic.close' | translate }}"
          (click)="closeProcessModal($event)"></button>
      }
      <!-- wrapper carries the tooltip: a disabled button gets no mouse events -->
      <span
        class="process-execute-wrapper"
        [attr.title]="
          !isProcessing && missingMandatoryParams.length > 0
            ? ('process.fillMandatoryField' | translate) + ' : [ ' + missingMandatoryParams.join(', ') + ' ]'
            : null
        ">
        <button
          data-cy="process-button"
          icon="fa fa-check"
          pButton
          type="button"
          [attr.data-cy-channelId]="channelId"
          [attr.data-cy-processId]="windowId"
          class="p-button p-button-primary {{ isExecuteDisabled ? 'iu-btn-disabled' : '' }}"
          [disabled]="isExecuteDisabled"
          label="{{ (activeIndex === 2 ? 'process.rerun' : 'process.execute') | translate }}"
          (click)="prepareExecuteProcess($event)"></button>
      </span>
    </div>
  </div>
</div>

<div [ngClass]="[!displayFormUI ? 'hidden' : '', 'specificModal']">
  <iu-modal-ui
    #specificModal
    [angularStyle]="customFormModalBodyCss"
    [isModalDisplay]="displayFormUI"
    [title]="specificWindowTitle"
    (closeModalEmitter)="updateModalDisplay({ display: { key: 'displayFormUI', value: false } })">
    <ng-template #vcrSpecific />
  </iu-modal-ui>
</div>
